import { useState, useEffect, lazy, Suspense } from 'react';
import type { Session } from '@supabase/supabase-js';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { CHUNK_RELOAD_FLAG_KEY } from './components/ErrorBoundary';

// The route guards and the always-mounted side-effect components below all
// reach the Supabase client (directly, or via lib/tenant, lib/platform,
// lib/mfa, lib/consent, lib/training…). Loading them lazily rather than with a
// static import is what keeps `@supabase/supabase-js` — GoTrue + realtime +
// storage + postgrest, ~210 kB — out of the entry chunk and its modulepreload
// list, so the public landing page ("/" for a logged-out visitor) paints
// without it. Each of these only runs async checks inside effects anyway, so
// mounting them a tick after first paint changes nothing observable.
const AuthGuard = lazy(() => import('./components/AuthGuard').then((m) => ({ default: m.AuthGuard })));
const SuperAdminGuard = lazy(() => import('./components/SuperAdminGuard').then((m) => ({ default: m.SuperAdminGuard })));
const ConsoleTracer = lazy(() => import('./components/ConsoleTracer').then((m) => ({ default: m.ConsoleTracer })));
const DesignTemplateLoader = lazy(() => import('./components/DesignTemplateLoader').then((m) => ({ default: m.DesignTemplateLoader })));
const ErrorReporter = lazy(() => import('./components/ErrorReporter').then((m) => ({ default: m.ErrorReporter })));
const MfaEnforcement = lazy(() => import('./components/MfaEnforcement').then((m) => ({ default: m.MfaEnforcement })));
const OnboardingEnforcement = lazy(() => import('./components/OnboardingEnforcement').then((m) => ({ default: m.OnboardingEnforcement })));
const RecordingIndicator = lazy(() =>
  import('./components/training/RecordingIndicator').then((m) => ({ default: m.RecordingIndicator }))
);
const VideoRecordingIndicator = lazy(() =>
  import('./components/training/VideoRecordingIndicator').then((m) => ({ default: m.VideoRecordingIndicator }))
);

const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword })));
const MfaVerify = lazy(() => import('./pages/MfaVerify').then((m) => ({ default: m.MfaVerify })));
const Home = lazy(() => import('./pages/Home').then((m) => ({ default: m.Home })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const Explorer = lazy(() => import('./pages/Explorer').then((m) => ({ default: m.Explorer })));
const Reslog = lazy(() => import('./pages/Reslog').then((m) => ({ default: m.Reslog })));
const Komihag = lazy(() => import('./pages/Komihag').then((m) => ({ default: m.Komihag })));
const MealPlanner = lazy(() => import('./pages/MealPlanner').then((m) => ({ default: m.MealPlanner })));
const UserProfile = lazy(() => import('./pages/UserProfile').then((m) => ({ default: m.UserProfile })));
const Help = lazy(() => import('./pages/Help').then((m) => ({ default: m.Help })));
const MyFeedback = lazy(() => import('./pages/MyFeedback').then((m) => ({ default: m.MyFeedback })));
const TravelPageView = lazy(() => import('./pages/TravelPageView').then((m) => ({ default: m.TravelPageView })));
const CreateTenant = lazy(() => import('./pages/CreateTenant').then((m) => ({ default: m.CreateTenant })));
const PendingApproval = lazy(() => import('./pages/PendingApproval').then((m) => ({ default: m.PendingApproval })));
const Welcome = lazy(() => import('./pages/Welcome').then((m) => ({ default: m.Welcome })));
const Guide = lazy(() => import('./pages/Guide').then((m) => ({ default: m.Guide })));
const Apply = lazy(() => import('./pages/Apply').then((m) => ({ default: m.Apply })));
const Privacy = lazy(() => import('./pages/Privacy').then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then((m) => ({ default: m.Terms })));
const Contact = lazy(() => import('./pages/Contact').then((m) => ({ default: m.Contact })));
const About = lazy(() => import('./pages/About').then((m) => ({ default: m.About })));
const LandingPackingList = lazy(() => import('./pages/LandingPackingList').then((m) => ({ default: m.LandingPackingList })));
const LandingCampingLog = lazy(() => import('./pages/LandingCampingLog').then((m) => ({ default: m.LandingCampingLog })));
const LandingGroupTrips = lazy(() => import('./pages/LandingGroupTrips').then((m) => ({ default: m.LandingGroupTrips })));
const LandingClubTripChecklist = lazy(() => import('./pages/LandingClubTripChecklist').then((m) => ({ default: m.LandingClubTripChecklist })));
const LandingGroupTripPlanner = lazy(() => import('./pages/LandingGroupTripPlanner').then((m) => ({ default: m.LandingGroupTripPlanner })));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe').then((m) => ({ default: m.Unsubscribe })));
const SetPassword = lazy(() => import('./pages/SetPassword').then((m) => ({ default: m.SetPassword })));
const Admin = lazy(() => import('./pages/Admin').then((m) => ({ default: m.Admin })));
const GmailOAuthCallback = lazy(() => import('./pages/GmailOAuthCallback').then((m) => ({ default: m.GmailOAuthCallback })));
const McpOAuthCallback = lazy(() => import('./pages/McpOAuthCallback').then((m) => ({ default: m.McpOAuthCallback })));
const OAuthConsent = lazy(() => import('./pages/OAuthConsent').then((m) => ({ default: m.OAuthConsent })));
const JoinTravel = lazy(() => import('./pages/JoinTravel').then((m) => ({ default: m.JoinTravel })));
const NotFound = lazy(() => import('./pages/NotFound').then((m) => ({ default: m.NotFound })));

function PageFallback() {
  return (
    <div className="flex justify-center items-center h-screen bg-surface">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

function App() {
  useEffect(() => {
    // A clean mount means the chunk-reload (if one just happened) worked -
    // clear the flag so a future deploy can still trigger one auto-reload.
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG_KEY);
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <ConsoleTracer />
        <RecordingIndicator />
        <VideoRecordingIndicator />
        <DesignTemplateLoader />
        <ErrorReporter />
        <MfaEnforcement />
        <OnboardingEnforcement />
      </Suspense>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* /welcome is the pre-restructure canonical URL for the landing page.
            Content now lives at "/" (RootIndex renders <Welcome /> for a
            logged-out visitor) so search engines consolidate on the root
            domain instead of splitting authority between "/" and "/welcome".
            A real server-side 301 (nginx.conf) handles direct/crawled hits;
            this client-side redirect only covers in-app navigation and old
            bookmarks that hit the SPA shell before nginx's rule (belt and
            braces — see docs/implementation-guide.md §15.2). */}
        <Route path="/welcome" element={<Navigate to="/" replace />} />
        <Route path="/packlista" element={<LandingPackingList />} />
        <Route path="/campinglogg" element={<LandingCampingLog />} />
        <Route path="/gruppresor" element={<LandingGroupTrips />} />
        <Route path="/checklista-klubbresa" element={<LandingClubTripChecklist />} />
        <Route path="/gruppresekollen" element={<LandingGroupTripPlanner />} />
        <Route path="/gruppresekollen/:variant" element={<LandingGroupTripPlanner />} />
        <Route path="/avregistrera" element={<Unsubscribe />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/* /oauth/consent: GoTrues OAuth 2.1-server (§69) omdirigerar hit —
            path styrs av supabase/config.toml auth.oauth_server.authorization_url_path.
            Sidan gör sin egen inloggnings-/adminkoll, inte AuthGuard (som
            skickar tenant-lösa användare till /pending). */}
        <Route path="/oauth/consent" element={<OAuthConsent />} />
        <Route path="/mfa-verify" element={<MfaVerify />} />
        <Route
          path="/admin"
          element={
            <SuperAdminGuard>
              <Admin />
            </SuperAdminGuard>
          }
        />
        {/* Googles OAuth-redirect när Gmail-kontot för fakturainkorgens
            hämtning ansluts (Admin → Drift → Gmail-hämtning). URI:n måste
            vara registrerad som authorized redirect URI på OAuth-klienten
            i Google Cloud Console. */}
        <Route
          path="/admin/gmail-callback"
          element={
            <SuperAdminGuard>
              <GmailOAuthCallback />
            </SuperAdminGuard>
          }
        />
        {/* OAuth-redirecten när en extern MCP-server ansluts
            (Admin → Systeminställningar → MCP-servrar). Denna URI registreras
            automatiskt hos servern via dynamisk klientregistrering. */}
        <Route
          path="/admin/mcp-oauth-callback"
          element={
            <SuperAdminGuard>
              <McpOAuthCallback />
            </SuperAdminGuard>
          }
        />
        <Route
          path="/pending"
          element={
            <AuthGuard requireTenant={false}>
              <PendingApproval />
            </AuthGuard>
          }
        />
        <Route
          path="/create-tenant"
          element={
            <AuthGuard requireTenant={false}>
              <CreateTenant />
            </AuthGuard>
          }
        />
        <Route path="/" element={<RootIndex />} />
        <Route
          path="/explorer"
          element={
            <AuthGuard>
              <Explorer />
            </AuthGuard>
          }
        />
        <Route
          path="/reslog"
          element={
            <AuthGuard>
              <Reslog />
            </AuthGuard>
          }
        />
        <Route
          path="/komihag"
          element={
            <AuthGuard>
              <Komihag />
            </AuthGuard>
          }
        />
        <Route
          path="/meal-planner"
          element={
            <AuthGuard>
              <MealPlanner />
            </AuthGuard>
          }
        />
        <Route
          path="/settings"
          element={
            <AuthGuard>
              <Settings />
            </AuthGuard>
          }
        />
        {/* requireTenant={false}: profilsidan är helt användarnivå (profil,
            lösenord, policygodkännande, MFA) och är dit OnboardingEnforcement
            skickar nya användare — som ofta inte har någon arbetsyta än. Med
            tenantkravet kvar hade de studsat mot /pending och aldrig kunnat
            slutföra lösenord + godkännande. */}
        <Route
          path="/profile"
          element={
            <AuthGuard requireTenant={false}>
              <UserProfile />
            </AuthGuard>
          }
        />
        <Route
          path="/help"
          element={
            <AuthGuard>
              <Help />
            </AuthGuard>
          }
        />
        {/* "Mina inskick": användaren granskar och godkänner AI-analysen av sin
            feedback. requireTenant={false} — inskicken hör till användaren, inte
            arbetsytan. */}
        <Route
          path="/feedback"
          element={
            <AuthGuard requireTenant={false}>
              <MyFeedback />
            </AuthGuard>
          }
        />
        {/* GH#122: a travel's shareable info page. requireTenant={false} —
            the page can belong to any tenant the visitor is a member of, not
            necessarily their "active" one; get_travel_page_by_slug() itself
            enforces membership and publish status. */}
        <Route
          path="/page/:slug"
          element={
            <AuthGuard requireTenant={false}>
              <TravelPageView />
            </AuthGuard>
          }
        />
        <Route
          path="/guide"
          element={
            <AuthGuard>
              <Guide />
            </AuthGuard>
          }
        />
        <Route
          path="/join-travel"
          element={
            <JoinTravel />
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

function RootIndex() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasTenant, setHasTenant] = useState(false);

  useEffect(() => {
    let active = true;
    let unsubscribe = () => {};

    // The Supabase client is imported here rather than at module scope so it
    // stays out of the entry chunk (see the note by the lazy component
    // declarations above). RootIndex shows its spinner until this resolves.
    (async () => {
      const [{ supabase }, { hasAnyTenant }] = await Promise.all([
        import('./lib/supabase'),
        import('./lib/tenant'),
      ]);
      if (!active) return;

      const checkSession = async () => {
        try {
          const { data } = await supabase.auth.getSession();
          if (!active) return;
          setSession(data.session);

          if (data.session) {
            const ok = await hasAnyTenant();
            if (active) {
              setHasTenant(ok);
            }
          }
        } catch (err) {
          console.error('Error checking session:', err);
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };

      await checkSession();

      const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
        if (!active) return;
        setSession(session);
        if (session) {
          const ok = await hasAnyTenant();
          if (active) {
            setHasTenant(ok);
          }
        }
      });
      unsubscribe = () => authListener.subscription.unsubscribe();
    })().catch((err) => {
      console.error('Error loading session check:', err);
      if (active) setLoading(false);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-surface">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!session) {
    return <Welcome />;
  }

  // Samma regel som i AuthGuard: utan arbetsyta går man till /pending, som
  // avgör om det finns en godkänd ansökan att skapa en arbetsyta utifrån.
  if (!hasTenant) {
    return <Navigate to="/pending" replace />;
  }

  return <Home />;
}

export default App;
